import { UserAPI } from '@/components/routes/auth/auth-page/helpers/requests/user'
import {
  DepartmentRoles,
  FacultyRoles,
  GroupRoles,
  roles
} from '@/config/roles'
import { User } from 'interfaces/user'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import _ from 'lodash'
import * as Sentry from '@sentry/vue'
import { useStudentStore } from '@/stores/student'

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)

  const isAuthenticated = computed(() => !_.isNil(user.value))
  const currentUser = computed(() => user.value)
  const userRoles = computed(() => user.value?.roles ?? [])
  const isAdmin = computed(() => userRoles.value.includes(roles.admin))
  const isStudent = computed(() => userRoles.value.includes(roles.student))
  const isDepHead = computed(() =>
    userRoles.value.includes(roles.departmentHead)
  )
  const isFakeAuth = computed(() => !!user.value?.actualUser)

  const groupRole = computed(() => user.value?.groups?.[0]?.UserGroup?.role)
  const facultiesHead = computed(() =>
    user.value?.faculties.filter((f) =>
      f.UserFaculty.roles.includes(FacultyRoles.dean)
    )
  )
  const departmentsHead = computed(() =>
    user.value?.departments.filter((d) =>
      d.UserDepartment.roles.includes(DepartmentRoles.depHead)
    )
  )

  const haveFacultiesHead = computed(() => !!facultiesHead.value?.length),
    haveDepsHead = computed(() => !!departmentsHead.value?.length),
    haveFacultyOrDepHead = computed(
      () => haveFacultiesHead.value || haveDepsHead.value
    )

  const isTeacher = computed(() => {
    return userRoles.value?.includes(roles.teacher)
  })
  const isGroupLeader = computed(() => {
    return groupRole.value === GroupRoles.leader
  })

  const isSED = computed(() => {
    return userRoles.value?.includes(roles.stats)
  })

  const curated = computed(() => user.value?.curated ?? []),
    isCurator = computed(() => curated.value.length > 0)

  const isAdminOrSED = computed(() => isAdmin.value || isSED.value)

  async function fetchCurrentUser(reload = false) {
    try {
      user.value = (await UserAPI.getCurrentUser(reload)).data.user
      Sentry.setUser({
        id: String(user.value!.id),
        name: user.value!.name,
        surname: user.value!.surname,
        midname: user.value!.midname,
        email: user.value!.email
      })
    } catch (err) {
      user.value = null
      Sentry.setUser(null)
    }
  }

  return {
    user,
    isAuthenticated,
    currentUser,
    userRoles,
    isAdmin,
    isStudent,
    isDepHead,
    isFakeAuth,
    fetchCurrentUser,
    facultiesHead,
    departmentsHead,
    haveFacultiesHead,
    haveDepsHead,
    isTeacher,
    isGroupLeader,
    isSED,
    student: useStudentStore(),
    curated,
    isCurator,
    haveFacultyOrDepHead,
    isAdminOrSED
  }
})
